import Swiper, { Manipulation, Mousewheel, Navigation } from 'swiper';
import 'swiper/css';
import renderProductCards from '../utils/render-product-cards.js';
import { getResults } from '../modules/searchanise.js';
import Product from '../models/Product.js';
import ProductCard from '../components/svelte/ProductCard.svelte';

const selectors = {
  productTab: '[data-product-tab]',
  productGroup: '[data-product-group]',
  carouselWrapper: '[data-product-carousel]',
};

class ProductCarousel extends HTMLElement {
  constructor() {
    super();

    this.createObserver();
  }

  createObserver() {
    this.observer = new IntersectionObserver((entries, observer) => {
      if (entries[0].isIntersecting) {
        this.render();
        observer.unobserve(this);
      }
    });

    this.observer.observe(this);
  }

  render() {
    this.productTab = this.querySelector(selectors.productTab);
    this.productGroup = this.querySelector(selectors.productGroup);
    this.productTabAll = this.querySelectorAll(selectors.productTab);
    this.productGroupAll = [...this.querySelectorAll(selectors.productGroup)];

    this.carouselWrappers = this.querySelectorAll(selectors.carouselWrapper);

    this.init();
  }

  init() {
    this.initCarousels();
    this.initTabs();
    renderProductCards();
  }

  initCarousels() {
    if (this.carouselWrappers) {
      this.carouselWrappers.forEach(wrapper => {
        const carouselObserver = new IntersectionObserver(
          (entries, observer) => {
            if (entries[0].isIntersecting) {
              ProductCarousel.renderCarousel(wrapper);
              observer.unobserve(wrapper);
            }
          }
        );

        carouselObserver.observe(wrapper);
      });
    }
  }

  initTabs() {
    this.productGroup.classList.remove('hidden');

    if (this.productTab) {
      this.productTab.classList.add('text-black');
      this.productTab.classList.remove('font__custom-grey');
    }

    if (this.productTabAll) {
      this.productTabAll.forEach(item => {
        item.addEventListener('click', e => {
          e.preventDefault();

          this.productTabAll.forEach(tab => {
            tab.classList.remove('text-black');
            tab.classList.add('font__custom-grey');
          });

          item.classList.remove('font__custom-grey');
          item.classList.add('text-black');

          this.productGroupAll.forEach(current => {
            current.classList.remove('hidden');
            current.classList.remove('block');
            if (item.dataset.productTab === current.dataset.productGroup) {
              current.classList.add('block');
            } else {
              current.classList.add('hidden');
            }
          });
        });
      });
    }
  }

  static renderCarousel(wrapper) {
    if (typeof wrapper.dataset.collectionHandle === 'undefined') {
      return;
    }

    // Only render the carousel once...
    if (wrapper.getAttribute('data-carousel-loaded') === 'true') {
      return;
    }

    // eslint-disable-next-line no-new
    new Swiper(wrapper, {
      modules: [Navigation, Mousewheel, Manipulation],
      freeMode: false,
      slidesPerView: 1.2,
      spaceBetween: 10,
      slidesOffsetAfter: 0,
      observer: true,
      preloadImages: false,
      mousewheel: {
        forceToAxis: true,
      },
      breakpoints: {
        1024: {
          slidesPerView: 4.2,
        },
      },
      on: {
        init(swiper) {
          ProductCarousel.loadProductsFromSearchanise(
            wrapper.dataset.collectionHandle,
            wrapper.dataset.collectionId,
            swiper
          );
        },

        async realIndexChange(swiper) {
          const page = Number(wrapper.dataset.collectionPage) + 1;
          // eslint-disable-next-line no-param-reassign
          wrapper.dataset.collectionPage = String(page);

          await ProductCarousel.loadProductsFromSearchanise(
            wrapper.dataset.collectionHandle,
            wrapper.dataset.collectionId,
            swiper,
            page
          );
        },
      },
      navigation: {
        nextEl: '.swiper-product-button-next',
        prevEl: '.swiper-product-button-prev',
      },
    });

    wrapper.setAttribute('data-carousel-loaded', 'true');
  }

  static loadProductsFromSearchanise(handle, id, swiper, page = 1) {
    getResults({
      collection: handle,
      maxResults: 8,
      sortBy: `collection_${id}_position`,
      sortOrder: 'asc',
      startIndex: page === 1 ? 0 : (page - 1) * 8,
    }).then(response => {
      // We're at the end – do nothing.
      if (response.currentItemCount === 0) {
        return;
      }

      response.items
        .map(item => Product.fromSearchanise(item))
        .forEach(product => {
          const dynamicSlideAlreadyExists = document.querySelector(
            `[data-dynamic-product-slide="page-${page}--${product.id}--${handle}"]`
          );

          // Avoid putting multiple product cards in the same slide if
          // there's a race condition when loading products, or if
          // the same page is loaded multiple times.
          if (dynamicSlideAlreadyExists) {
            return;
          }

          swiper.appendSlide([
            `<div class="swiper-slide lg:max-w-[33%]">
              <div class="relative" data-dynamic-product-slide="page-${page}--${product.id}--${handle}"></div>
            </div>`,
          ]);

          // eslint-disable-next-line no-new
          new ProductCard({
            target: document.querySelector(
              `[data-dynamic-product-slide="page-${page}--${product.id}--${handle}"]`
            ),
            props: {
              product,
              doNotRender: ['reviews'],
            },
          });

          swiper.updateSize();
        });

      // If it's the initial render, remove the ghost cards
      if (page === 1) {
        const slidesToRemove = [];
        swiper.slides.forEach((slide, i) => {
          if (slide.querySelector('[data-ghost-card]')) {
            slidesToRemove.push(i);
          }
        });

        swiper.removeSlide(slidesToRemove);
      }

      swiper.update();
    });
  }
}

customElements.define('product-carousel', ProductCarousel);
